import * as React from "react";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import { useOfferMode } from "@nju/data-access-portal-offers";
import { IMobileOrchestrator } from "@nju/data-access-portal-orchestrator";
import { IPaymentMethodFormType } from "@nju/data-access-portal-payment";
import { FAQButton } from "@nju/feature-portal-help";
import { paymentFormMachine } from "@nju/feature-portal-payment";
import { ErrorMessage } from "@nju/form";
import { portalRoutes } from "@nju/routes";
import { BlockText, Heading, Panel, Separator, Text } from "@nju/ui";
import { formatPrice } from "@nju/utils";
import { Buttons } from "./partials/Buttons";
import { PaymentVariants } from "./partials/PaymentVariants";

interface IPaymentFormProps {
  orchestrator: IMobileOrchestrator;
}

export function PaymentForm({ orchestrator }: IPaymentFormProps) {
  const router = useRouter();
  const mode = useOfferMode();
  const { id, orderSummary, isFreeFromActivationFee, userId } = orchestrator;

  const [{ context, matches }, send] = useMachine(paymentFormMachine, {
    actions: {
      redirectToDashboard: () => {
        router.push(
          portalRoutes.mojeKonto.index.get({
            mode,
            ...(userId && { userId }),
          }),
        );
      },
      redirectTiPayByLinkErrorPage: () => {
        router.push(
          portalRoutes.mojeKonto.platnosc.bladPlatnosciOnline.get({
            mode,
            ...(userId && { userId }),
          }),
        );
      },
    },
    context: {
      ...paymentFormMachine.context,
      amount: orderSummary?.totalOrderPrice,
      orchestratorId: id,
      onSubmit: (transactionLink) => {
        if (!transactionLink || transactionLink === "MOCK") {
          router.push(
            portalRoutes.mojeKonto.index.get({
              mode,
              ...(userId && { userId }),
            }),
          );
        } else {
          window.sessionStorage.setItem(
            "external-payment-flow",
            "mobile-fulfillment",
          );
          window.location.assign(transactionLink);
        }
      },
    },
  });

  const { paymentMethod, formError } = context;

  const handleSetValue = React.useCallback(
    (value: IPaymentMethodFormType) => {
      send({ type: "SET_PAYMENT_METHOD", value });
    },
    [send],
  );

  const handlePayByLink = React.useCallback(() => {
    send({ type: "PAY_BY_LINK" });
  }, [send]);

  const handleAddCreditCard = React.useCallback(() => {
    send({ type: "ADD_CREDIT_CARD" });
  }, [send]);

  return (
    <Panel>
      <Heading font="ttfirs" tone="neutral" variant="h2" weight="bold">
        wybierz sposób płatności
      </Heading>
      <form>
        <Separator size="s" tone="invisible" />
        <PaymentVariants handleSetValue={handleSetValue} />
        {formError && (
          <>
            <Separator size="xs" tone="invisible" />
            <ErrorMessage message={formError} />
          </>
        )}
        <Separator size="xs" tone="invisible" />
        <FAQButton
          categoryId="NJU_WWW_PAYMENT_METHOD"
          title="jak wygląda proces płatności?"
        />
        <Separator size="s" />
        {orderSummary && (
          <>
            <BlockText size={{ "@onlyMobile": "m", "@fromTablet": "l" }}>
              pierwsza opłata:{" "}
              <Text
                size={{ "@onlyMobile": "m", "@fromTablet": "l" }}
                weight="bold"
              >
                {formatPrice(orderSummary.totalOrderPrice)} zł
              </Text>
              <Separator size="m" />
            </BlockText>
            {!isFreeFromActivationFee && (
              <Text>
                Opłata za aktywację doliczana jest wyłącznie do pierwszej
                płatności, później płacisz już tylko{" "}
                {formatPrice(orderSummary.totalRecurringOrderPrice)} zł
                miesięcznie.
              </Text>
            )}
          </>
        )}
        <Separator size="s" tone="invisible" />
        <Buttons
          handleAddCreditCard={handleAddCreditCard}
          handlePayByLink={handlePayByLink}
          isLoading={!matches("init")}
          paymentMethod={paymentMethod}
          userId={userId}
        />
      </form>
    </Panel>
  );
}
